import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { adminApi } from '@/api';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  ErrorState,
  Field,
  Icon,
  Input,
  LinkButton,
  LoadingState,
  Modal,
  PageHeader,
  Pagination,
  SegmentedTabs,
  Select,
  StatTile,
  useToast,
} from '@/components/ui';
import { Tabs } from '@/components/shell';
import { CustomerPicker } from '@/features/billing/CustomerPicker';
import { InvoiceTable } from '@/features/billing/InvoiceTable';
import { StudentSubscriptionsList } from '@/features/billing/StudentSubscriptionsList';
import { InvoiceDownloadButton } from '@/features/studentBilling/InvoiceDownloadButton';
import {
  // formatAmounts, -- used by the hidden money tiles
  isStudentPlan,
  planGrantLines,
  formatPrice,
  statusLabel,
  statusTone,
} from '@/features/billing/money';
import { errorMessage } from '@/hooks/useApiError';
import { paths } from '@/routes/paths';
import { formatDate } from '@/utils/format';

/**
 * The platform's billing position: what is sold, who is on what, and what is
 * owed.
 *
 * Every figure is counted from subscriptions and invoices rather than read
 * from a stored total, the same way the analytics module works — a cached
 * revenue number is one that can be wrong without anybody noticing.
 */
// `customer` is the picked `{ id, label, detail }`, or null until one is chosen.
const EMPTY_ONE_OFF = { kind: 'company', customer: null, description: '', amount: '' };

/** One section at a time; the open one is in the URL so a link can land on it. */
const SECTIONS = [
  { key: 'invoices', label: 'Payments & invoices', icon: 'banknote' },
  { key: 'subscriptions', label: 'Subscriptions', icon: 'repeat' },
  { key: 'plans', label: 'Plans', icon: 'package' },
];

/**
 * Companies and students are billed apart -- different plans, usually a
 * different currency -- so the page shows one audience at a time.
 */
const CUSTOMER_TABS = [
  { key: 'company', label: 'Companies' },
  { key: 'student', label: 'Students' },
];

/** Invoices per page in the admin roster. */
const INVOICE_PAGE_SIZE = 20;

export function AdminBillingPage() {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const section = SECTIONS.some((tab) => tab.key === searchParams.get('tab'))
    ? searchParams.get('tab')
    : 'invoices';
  const customer = searchParams.get('customer') === 'student' ? 'student' : 'company';
  const toast = useToast();
  const [paying, setPaying] = useState(null);
  const [reference, setReference] = useState('');
  const [voiding, setVoiding] = useState(null);
  const [reason, setReason] = useState('');
  const [issuing, setIssuing] = useState(false);
  const [oneOff, setOneOff] = useState(EMPTY_ONE_OFF);
  const [invoiceStatus, setInvoiceStatus] = useState('OPEN');
  // The Companies / Students tab now picks the customer; the old dropdown
  // with an "All" option is commented out below.
  const invoiceCustomer = customer;
  const [invoicePage, setInvoicePage] = useState(1);
  const [studentPlanId, setStudentPlanId] = useState('');

  function setUrl({ nextSection = section, nextCustomer = customer }) {
    const next = {};
    if (nextSection !== 'invoices') next.tab = nextSection;
    if (nextCustomer === 'student') next.customer = 'student';
    setSearchParams(next);
  }

  const overview = useQuery({
    queryKey: ['admin-billing-overview'],
    queryFn: adminApi.getBillingOverview,
  });
  const plans = useQuery({ queryKey: ['admin-plans'], queryFn: adminApi.listBillingPlans });
  const subscriptions = useQuery({
    queryKey: ['admin-subscriptions'],
    queryFn: () => adminApi.listSubscriptions({ page_size: 20 }),
  });
  const invoices = useQuery({
    queryKey: ['admin-invoices', invoiceStatus, invoiceCustomer, invoicePage],
    queryFn: () =>
      adminApi.listAllInvoices({
        page: invoicePage,
        page_size: INVOICE_PAGE_SIZE,
        ...(invoiceStatus ? { status: invoiceStatus } : {}),
        ...(invoiceCustomer ? { customer: invoiceCustomer } : {}),
      }),
  });

  function refresh() {
    ['admin-billing-overview', 'admin-invoices', 'admin-subscriptions'].forEach((key) =>
      queryClient.invalidateQueries({ queryKey: [key] }),
    );
  }

  const voidInvoice = useMutation({
    mutationFn: ({ id, reason }) => adminApi.voidInvoice(id, reason),
    onSuccess: () => {
      toast.success('Invoice voided');
      setVoiding(null);
      setReason('');
      refresh();
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const issue = useMutation({
    mutationFn: () =>
      adminApi.issueInvoice({
        ...(oneOff.kind === 'student'
          ? { studentId: oneOff.customer.id }
          : { companyId: oneOff.customer.id }),
        description: oneOff.description.trim(),
        amountCents: Math.round(Number(oneOff.amount) * 100),
      }),
    onSuccess: (invoice) => {
      toast.success(`${invoice.number} issued`);
      setIssuing(false);
      setOneOff(EMPTY_ONE_OFF);
      refresh();
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const markPaid = useMutation({
    mutationFn: ({ id, note }) => adminApi.markInvoicePaid(id, note),
    onSuccess: () => {
      toast.success('Invoice marked paid');
      setPaying(null);
      setReference('');
      refresh();
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  // Kept for the switched-off "Run renewals" button (rename back to `renewals`).
  const _renewals = useMutation({
    mutationFn: adminApi.runRenewals,
    onSuccess: (result) => {
      toast.success(
        `${result.renewed} renewed, ${result.downgraded} changed plan, ${result.invoices_issued} invoiced.`,
      );
      refresh();
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  if (overview.isPending) return <LoadingState label="Loading billing…" />;
  if (overview.isError) {
    return (
      <ErrorState message={errorMessage(overview.error)} onRetry={() => void overview.refetch()} />
    );
  }

  const totals = overview.data;

  return (
    <div>
      <PageHeader
        title="Billing"
        description="Plans, subscribers and invoices, counted from real rows."
        // "One-off invoice" and "Run renewals" are switched off for now: the
        // scheduler runs renewals on its own. To bring them back, restore:
        //   action={<>
        //     <Button variant="secondary" size="sm" onClick={() => setIssuing(true)}>
        //       One-off invoice</Button>
        //     <Button variant="secondary" size="sm" onClick={() => renewals.mutate()}
        //       isLoading={renewals.isPending}>Run renewals</Button>
        //   </>}
      />

      <SegmentedTabs
        label="Customer"
        className="mb-6"
        tabs={CUSTOMER_TABS}
        value={customer}
        onChange={(key) => {
          setInvoicePage(1);
          setUrl({ nextCustomer: key });
        }}
      />

      {/* Money figures, hidden: the tiles count customers; amounts are on the
          invoices themselves.
        <StatTile
          label="Monthly recurring (companies)"
          value={formatAmounts(totals.company_mrr_by_currency, 'EUR')}
          hint="Per currency · yearly plans divided down to a month"
        />
        <StatTile
          label="Outstanding"
          value={formatAmounts(totals.outstanding_by_currency, 'EUR')}
          hint={`Companies and students, per currency · ${totals.overdue_invoices} overdue`}
        />
      */}
      {customer === 'student' ? (
        <div className="mb-6 grid gap-4 sm:grid-cols-3">
          <StatTile
            label="Paying students"
            value={totals.paying_students ?? 0}
            hint="On a running paid plan"
          />
          <StatTile
            label="Outstanding students"
            value={totals.students_owing ?? 0}
            hint="With an unpaid invoice"
          />
          <StatTile
            label="Overdue students"
            value={totals.overdue_students ?? 0}
            hint="With an invoice past its due date"
          />
        </div>
      ) : (
        <div className="mb-6 grid gap-4 sm:grid-cols-3">
          <StatTile
            label="Paying companies"
            value={totals.paying_companies ?? 0}
            hint="On a paid plan"
          />
          <StatTile
            label="Outstanding companies"
            value={totals.companies_owing ?? 0}
            hint="With an unpaid invoice"
          />
          <StatTile
            label="Overdue companies"
            value={totals.overdue_companies ?? 0}
            hint="With an invoice past its due date"
          />
        </div>
      )}

      <Tabs
        label="Billing section"
        tabs={SECTIONS}
        value={section}
        onChange={(key) => setUrl({ nextSection: key })}
        className="mb-4"
      />

      {section === 'plans' && (
        <Card>
          <CardHeader
            title="Plans"
            description="What is on sale and who is on it."
            action={
              <LinkButton to={paths.admin.plans} variant="secondary" size="sm">
                Manage plans
              </LinkButton>
            }
          />
          {plans.isPending ? (
            <LoadingState label="Loading plans…" />
          ) : plans.isError ? (
            <SectionError
              message={errorMessage(plans.error)}
              onRetry={() => void plans.refetch()}
            />
          ) : (plans.data ?? []).length === 0 ? (
            <EmptyState
              compact
              title="No plans yet"
              description="Create one from the plans screen and it appears on the public pricing page."
            />
          ) : (
            <ul className="divide-line divide-y">
              {(plans.data ?? [])
                .filter((plan) => isStudentPlan(plan) === (customer === 'student'))
                .map((plan) => (
                  <li key={plan.code} className="py-3">
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <span className="text-ink font-medium">{plan.name}</span>
                        <Badge tone={isStudentPlan(plan) ? 'brand' : 'neutral'}>
                          {isStudentPlan(plan) ? 'Students' : 'Companies'}
                        </Badge>
                        {!plan.is_active && <Badge>Retired</Badge>}
                        {!plan.is_public && <Badge tone="warning">Private</Badge>}
                      </div>
                      <span className="text-ink-muted text-sm">
                        {formatPrice(plan.price_cents, plan.currency, plan.interval)} ·{' '}
                        {plan.subscriber_count} on it
                      </span>
                    </div>
                    <p className="text-ink-muted text-caption mt-1">
                      {planGrantLines(plan).join(' · ')}
                    </p>
                  </li>
                ))}
            </ul>
          )}
        </Card>
      )}

      {section === 'subscriptions' && customer === 'student' && (
        <StudentSubscriptionsList planId={studentPlanId} onPlanChange={setStudentPlanId} />
      )}

      {section === 'subscriptions' && customer === 'company' && (
        <Card>
          <CardHeader
            title="Subscribers"
            description="Newest first."
            action={
              <LinkButton to={paths.admin.subscriptions} variant="secondary" size="sm">
                Manage all
              </LinkButton>
            }
          />
          {subscriptions.isPending ? (
            <LoadingState label="Loading subscribers…" />
          ) : subscriptions.isError ? (
            <SectionError
              message={errorMessage(subscriptions.error)}
              onRetry={() => void subscriptions.refetch()}
            />
          ) : (subscriptions.data?.data ?? []).length === 0 ? (
            <EmptyState compact title="Nobody is on a paid plan yet" />
          ) : (
            <ul className="divide-line divide-y">
              {(subscriptions.data?.data ?? []).map((row) => (
                <li key={row.id} className="flex items-center justify-between gap-3 py-3">
                  <div className="min-w-0">
                    <Link
                      to={paths.admin.subscriptionDetail(row.id)}
                      className="text-ink block truncate text-sm font-medium hover:underline"
                    >
                      {row.company_name}
                    </Link>
                    <p className="text-ink-muted text-caption">
                      {row.plan_name} · renews {formatDate(row.current_period_end)}
                      {row.cancel_at_period_end && ' · ending'}
                    </p>
                  </div>
                  <Badge tone={statusTone(row.status)}>{statusLabel(row.status)}</Badge>
                </li>
              ))}
            </ul>
          )}
        </Card>
      )}

      {section === 'invoices' && (
        <Card>
          <CardHeader
            title="Invoices"
            description="With no payment vendor configured, invoices are settled by bank transfer and marked paid here."
            action={
              <div className="flex flex-wrap gap-2">
                {/* The customer dropdown is replaced by the Companies /
                    Students tabs at the top of the page.
                <Select
                  value={invoiceCustomer}
                  onChange={(event) => {
                    setInvoiceCustomer(event.target.value);
                    setInvoicePage(1);
                  }}
                  aria-label="Filter invoices by customer"
                >
                  <option value="">All</option>
                  <option value="company">Companies</option>
                  <option value="student">Students</option>
                </Select>
                */}
                <Select
                  value={invoiceStatus}
                  onChange={(event) => {
                    setInvoiceStatus(event.target.value);
                    setInvoicePage(1);
                  }}
                  aria-label="Filter invoices"
                >
                  <option value="OPEN">Open</option>
                  <option value="PAID">Paid</option>
                  <option value="VOID">Voided</option>
                  <option value="">All</option>
                </Select>
              </div>
            }
          />
          {invoices.isPending ? (
            <LoadingState label="Loading invoices…" />
          ) : invoices.isError ? (
            <SectionError
              message={errorMessage(invoices.error)}
              onRetry={() => void invoices.refetch()}
            />
          ) : (invoices.data?.data ?? []).length === 0 ? (
            /* The shared table's own empty message is written for a company
             looking at its own bill, so the admin roster says its own. */
            <EmptyState
              compact
              title={invoiceStatus ? 'No invoices match this filter' : 'No invoices yet'}
            />
          ) : (
            <InvoiceTable
              invoices={invoices.data?.data ?? []}
              showCustomer
              action={(invoice) => (
                <div className="flex items-center justify-end gap-1">
                  {invoice.status === 'OPEN' && (
                    <>
                      <Button
                        variant="ghostPositive"
                        size="xs"
                        title={`Mark invoice ${invoice.number} paid`}
                        onClick={() => setPaying(invoice)}
                      >
                        <Icon name="banknote" size={14} />
                        Mark paid
                      </Button>
                      {/* Void is switched off for now. To bring it back:
                      <Button variant="ghostDanger" size="xs"
                        title={`Void invoice ${invoice.number}`}
                        onClick={() => setVoiding(invoice)}>
                        <Icon name="close" size={14} /> Void
                      </Button> */}
                    </>
                  )}
                  <InvoiceDownloadButton invoice={invoice} download={adminApi.downloadInvoicePdf} />
                </div>
              )}
            />
          )}
          {invoices.data?.meta && invoices.data.meta.total_pages > 1 && (
            <Pagination
              page={invoices.data.meta.page}
              pageSize={INVOICE_PAGE_SIZE}
              totalPages={invoices.data.meta.total_pages}
              totalItems={invoices.data.meta.total_items}
              onPageChange={setInvoicePage}
            />
          )}
        </Card>
      )}

      <Modal
        open={paying !== null}
        title={`Mark ${paying?.number ?? ''} paid`}
        onClose={() => setPaying(null)}
      >
        <p className="text-ink-muted mb-4 text-sm">
          This records that money arrived. It is audited against your account, so put something here
          that identifies the transfer.
        </p>
        <Field label="Payment reference">
          <Input
            value={reference}
            onChange={(event) => setReference(event.target.value)}
            placeholder="Bank transfer 2026-08-20, ref INV-2026-000012"
          />
        </Field>
        <div className="mt-6 flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setPaying(null)}>
            Cancel
          </Button>
          <Button
            disabled={reference.trim().length === 0}
            isLoading={markPaid.isPending}
            onClick={() => markPaid.mutate({ id: paying.id, note: reference.trim() })}
          >
            Mark paid
          </Button>
        </div>
      </Modal>

      <Modal
        open={voiding !== null}
        title={`Void ${voiding?.number ?? ''}`}
        description="For an invoice that should never have been issued. It is not a refund and does not undo a payment — it cancels a bill nobody should pay."
        onClose={() => setVoiding(null)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setVoiding(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              disabled={reason.trim().length < 4}
              isLoading={voidInvoice.isPending}
              onClick={() => voidInvoice.mutate({ id: voiding.id, reason: reason.trim() })}
            >
              Void it
            </Button>
          </>
        }
      >
        <Field label="Reason">
          <Input
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="Issued against the wrong company"
          />
        </Field>
      </Modal>

      <Modal
        open={issuing}
        title="Issue a one-off invoice"
        description="For something the subscription cycle does not cover — setup work, an agreed overage, a negotiated extra. It carries no plan and no period, so nothing counts it as subscription revenue."
        onClose={() => setIssuing(false)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setIssuing(false)}>
              Cancel
            </Button>
            <Button
              disabled={
                !oneOff.customer ||
                oneOff.description.trim().length < 4 ||
                !(Number(oneOff.amount) > 0)
              }
              isLoading={issue.isPending}
              onClick={() => issue.mutate()}
            >
              Issue
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <div>
            <p className="text-ink mb-1.5 text-sm font-medium">Bill</p>
            <SegmentedTabs
              label="Customer type"
              tabs={[
                { key: 'company', label: 'A company' },
                { key: 'student', label: 'A student' },
              ]}
              value={oneOff.kind}
              // A company picked under one tab is not a student under the other.
              onChange={(kind) => setOneOff({ ...oneOff, kind, customer: null })}
            />
          </div>
          <CustomerPicker
            kind={oneOff.kind}
            value={oneOff.customer}
            onChange={(customer) => setOneOff({ ...oneOff, customer })}
          />
          <Field label="What it is for">
            <Input
              value={oneOff.description}
              onChange={(event) => setOneOff({ ...oneOff, description: event.target.value })}
              placeholder="Onboarding and data migration, as agreed"
            />
          </Field>
          <Field label="Amount">
            <Input
              type="number"
              min="0.01"
              step="0.01"
              value={oneOff.amount}
              onChange={(event) => setOneOff({ ...oneOff, amount: event.target.value })}
              placeholder="250.00"
            />
          </Field>
          <p className="text-ink-muted text-xs">
            The currency follows the customer&apos;s own plan — a student with no plan is billed in
            INR. Paying a one-off never switches a plan on.
          </p>
        </div>
      </Modal>
    </div>
  );
}

/** A secondary query that failed, said inside its card rather than over the page. */
function SectionError({ message, onRetry }) {
  return (
    <Alert tone="error" title="Could not load this">
      <p>{message}</p>
      <Button variant="secondary" size="xs" className="mt-2" onClick={onRetry}>
        Try again
      </Button>
    </Alert>
  );
}
